import type { ReplaceLink, Tok } from '@d/markdownit/plugins/markdownItReplaceLink'
import { manualPublicApi } from '@d/sdk'
import { createLinkFromSameStoragelink } from '@d/util/storagelink'

export interface ManualMetadata {
  sort: Array<{ id: number, name: string }>
  accessKey: string
  password: string
}

const count = ref(0)
const route = useRoute('manual-shared-tenant-key')

export function replaceLink(workingDir: string, password?: string): ReplaceLink {
  return async (token: Tok) => {
    if (token.type === 'image') {
      const linkIndex = token.attrs?.findIndex?.(e => e[0] === 'src')
      if (linkIndex === undefined || linkIndex === -1)
        return
      const link = token.attrs![linkIndex]?.[1]
      if (link === undefined)
        return
      const accessKey = route.params.key
      const id = ++count.value
      token.attrs!.push(['id', `img${id.toString()}`])
      return await displayProtectedImage(`img${id.toString()}`, link, workingDir, accessKey, token, password)
    }
    else if (token.tag === 'a') {
      // decode href so percent-encoded absolute URLs are detected
      let rawHref = token.attrGet('href') || null
      if (rawHref) {
        try {
          rawHref = decodeURIComponent(rawHref)
        }
        catch {
          // ignore
        }
        rawHref = rawHref.replaceAll('%5B', '[').replaceAll('%5D', ']')
      }
      const target = rawHref || null
      if (!target)
        return

      // don't rewrite absolute http(s) links
      if (/^https?:\/\//i.test(target))
        return

      token.attrSet('href', createLinkFromSameStoragelink(target))
    }
  }
}

async function displayProtectedImage(
  imageId: string,
  imageUrl: string,
  workingDir: string,
  accessKey: string,
  token: Tok,
  password?: string,
): Promise<void> {
  const res = await manualPublicApi.getImageRaw({ tenant: route.params.tenant, imageName: decodeURIComponent(imageUrl.slice('/assets'.length)), workingDir, accessKey, password })
  const blob = await res.raw.blob()

  if (!blob)
    return

  const imageElement = window.document.getElementById(imageId) as HTMLImageElement | null
  if (imageElement !== null) {
    imageElement.src = URL.createObjectURL(blob)
  }
  else {
    token.attrSet('src', URL.createObjectURL(blob))
  }
}

export const manual = ref<{ title?: string, docs: { path: string, title: string }[] }>({ title: undefined, docs: [] })

export async function fetchManuals(accessKey: string, password?: string): Promise<{ documents: { path: string, title: string }[], paths: string[], title: string | undefined }> {
  const doc = await manualPublicApi.getManual(route.params.tenant, accessKey, password)
  const documents: { path: string, title: string }[] = []
  for (const d of doc.paths) {
    if (d === '/index.md') {
      documents.push({ path: d, title: doc.title })
    }
    else {
      documents.push({ path: d, title: d.slice(1, -'.md'.length) })
    }
  }
  return { documents, paths: doc.paths, title: doc.title }
}
